import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Crown, Download } from "lucide-react";
import { useSettings } from "@/hooks/useSettings";
import { resolveImage } from "@/lib/api";

const links = [
  { to: "/", label: "Home" },
  { to: "/notices", label: "Notice Board" },
  { to: "/top-agencies", label: "Top Agencies" },
  { to: "/resellers", label: "Resellers" },
  { to: "/recharge", label: "Recharge" },
  { to: "/official-host", label: "Become Host" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const settings = useSettings();
  const navigate = useNavigate();

  const goDownload = () => {
    if (settings.app_download_link) window.open(settings.app_download_link, "_blank");
    else navigate("/official-host");
  };
  const hasApp = !!settings.app_download_link;

  return (
    <header className="glass-nav sticky top-0 z-50">
      <div className="h-0.5 w-full bg-gradient-to-r from-gold/70 via-emerald/60 to-gold/70" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <div className="flex h-16 items-center justify-between sm:h-20">
          <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo">
            {settings.logo_url ? (
              <img src={resolveImage(settings.logo_url)} alt="logo" className="h-9 w-9 rounded-lg object-cover" />
            ) : (
              <span className="flex h-9 w-9 items-center justify-center rounded-lg gold-gradient">
                <Crown className="h-5 w-5 text-obsidian" />
              </span>
            )}
            <span className="font-display text-xl font-extrabold gold-text tracking-wide sm:text-2xl">
              {settings.site_name || "Golden Live"}
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    isActive ? "bg-emerald/20 text-gold" : "text-slate-300 hover:text-gold"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={goDownload}
              data-testid="nav-download-app-btn"
              className="hidden items-center gap-2 rounded-full gold-gradient px-5 py-2.5 text-sm font-bold text-obsidian transition-transform hover:scale-[1.03] sm:flex"
            >
              <Download className="h-4 w-4" /> {hasApp ? "Download App" : "Become a Host"}
            </button>
            <button
              className="rounded-lg p-2 text-gold lg:hidden"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="border-t border-gold/10 bg-obsidian/95 px-4 py-4 lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-lg px-4 py-3 text-base font-semibold ${
                    isActive ? "bg-emerald/25 text-gold" : "text-slate-200"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button
              onClick={() => { setOpen(false); goDownload(); }}
              className="mt-2 flex items-center justify-center gap-2 rounded-full gold-gradient px-5 py-3 text-base font-bold text-obsidian"
            >
              <Download className="h-5 w-5" /> {hasApp ? "Download Golden Live App" : "Become a Golden Live Host"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
